import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  CheckCircle2, 
  AlertTriangle,
  FileText,
  Users,
  RefreshCw,
  Download,
  Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComplianceStatusTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("executiveToken") || localStorage.getItem("chroToken") || localStorage.getItem("authToken")}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/executive-enhanced/compliance-status`, getAuthHeaders());
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching compliance data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C4A265]" />
      </div>
    );
  }

  const { summary, byRegion } = data;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1a3a3a]">Regional Compliance Status</h2>
          <p className="text-sm text-muted-foreground">Voluntary participation and consent tracking across all regions</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export Compliance Report
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-[#1a3a3a]" />
              <span className="text-sm font-medium text-muted-foreground">Total Employees</span>
            </div>
            <p className="text-3xl font-bold text-[#1a3a3a]">{summary.totalEmployees}</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-muted-foreground">Consent Records on File</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{summary.consentRecordsOnFile}</p>
            <p className="text-sm text-muted-foreground">{summary.consentPercentage}% documented</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-emerald-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Union Consultations</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{summary.unionConsultationsComplete}/{summary.unionConsultationsTotal}</p>
            <p className="text-sm text-emerald-600 font-medium">Complete ✓</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-muted-foreground">Compliance Score</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{summary.consentPercentage}%</p>
            <Progress value={summary.consentPercentage} className="h-2 mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Regional Compliance Status */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#C4A265]" />
            Compliance by Region
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {byRegion.map((region, i) => {
              const statusColors = {
                complete: { bg: "bg-emerald-500", text: "text-emerald-700" },
                warning: { bg: "bg-amber-500", text: "text-amber-700" },
                critical: { bg: "bg-red-500", text: "text-red-700" }
              };
              const colors = statusColors[region.status] || statusColors.warning;
              
              return (
                <div key={i} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3 w-48">
                    <span className="text-2xl">{region.flag}</span>
                    <div>
                      <p className="font-semibold">{region.regionName}</p>
                      <Badge variant="outline" className="text-xs">
                        {region.complianceType}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Progress value={region.participationRate} className="flex-1 h-3" />
                      <span className="font-bold text-lg w-16 text-right">{region.participationRate}%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {region.participating} of {region.totalEmployees} employees participating
                    </p>
                  </div>
                  
                  <div className="w-24 text-center">
                    {region.status === "complete" ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Complete
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Warning
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Compliance Notes */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">North America - Standard Protocol</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                {summary.totalEmployees > 0 ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-slate-400" />}
                Standard assessment protocol applies
              </li>
              <li className="flex items-center gap-2">
                {summary.totalEmployees > 0 ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-slate-400" />}
                No special consent requirements
              </li>
              <li className="flex items-center gap-2">
                {summary.totalEmployees > 0 ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-slate-400" />}
                {summary.totalEmployees > 0 ? 'All employees auto-enrolled' : 'Awaiting employees'}
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Caribbean - Voluntary Consent</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                {summary.unionConsultationsComplete > 0 ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-slate-400" />}
                {summary.unionConsultationsComplete > 0 ? 'Union consultations completed' : 'Union consultations pending'}
              </li>
              <li className="flex items-center gap-2">
                {summary.consentRecordsOnFile > 0 ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-slate-400" />}
                {summary.consentRecordsOnFile > 0 ? 'Individual consent documented' : 'Consent records pending'}
              </li>
              <li className="flex items-center gap-2">
                {(summary.totalEmployees - summary.consentRecordsOnFile) > 0 ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <Clock className="w-4 h-4 text-slate-400" />}
                {summary.totalEmployees > 0 ? `${summary.totalEmployees - summary.consentRecordsOnFile} pending consent records` : 'Awaiting employees'}
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
